import React, { useEffect, useMemo, useState } from 'react';
import { navigate } from 'gatsby-link';
import { ComparisonTableContentfulModel } from 'src/api/models/ComparisonTable';
import DynamicComponents from 'src/functions/dynamicComponents';
import { layout } from 'src/styles/layout';
import styled from 'styled-components';
import { getStyleObjectFromString } from 'src/functions/getStyleObjectFromString'
import PolicyLink from 'elements/links/Link.policies'
import { propagateQuerytoExternalLinks, urlIsExternal, reApplyTrackingToUrl } from './propagateQueryParams';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';


export interface MarkupValue {
  type: 'text';
  value: string;
}

export interface MarkupElement {
  type: 'element';
  tagName: 'a' | 'p' | 'em' | 'blockquote' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'strong' | 'ul' | 'ol' | 'li' | 'img' | 'table' | 'thead' | 'tbody' | 'tr' | 'th' | 'td' | 'br' ;
  properties: {
    href?: string;
    src?: string;
    style?: any;
    id?: string;
  }
  children: (MarkupElement | MarkupValue)[]
}

export type Template = 'legalArticle' | 'resource' | 'caseStudy' | 'encyclopedia' | 'dynamicPage';

const TemplateSpacer = styled.br`
  display: block;
  content: "";
`

const TemplateBlockQuote = styled.div`
  position: relative;
  padding-left: 80px;
  box-sizing: border-box;

  &:before{
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 30px;
    height: 100%;
    background-position: 50%;
    background-size: contain;
    background-repeat: no-repeat;
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTIyLjM1MiA2Mi43MzFsLTIwLjYwNC0yLjhMMTguOTI4IDgwbDMuNDI0LTE3LjI2OXoiIGZpbGw9IiMyMjNBMzMiLz48cGF0aCBkPSJNMjIuMzUyIDQyLjc0MmwtMjAuNjA0IDIuOCAxNy4xOC0yMC4wNjggMy40MjQgMTcuMjY4eiIgZmlsbD0iIzFDQUJCRiIvPjxwYXRoIGQ9Ik0yNi45MjUgMi41NTdMNy4wODggMTUuMzc5Ljg5NCA4LjgxIDEuOTIgMS45MjVsMjUuMDA2LjYzeiIgZmlsbD0iI0ZGNzA2MiIvPjwvc3ZnPg==);
  }

  & > p {
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.4;
    font-family: Walsheim;
  }
`

const TemplateImagePair = styled.div`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  left: 0;
  margin: 1rem 0 1.5rem;

  & > img, & > div {
    max-width: calc(50% - 18px) !important;
    width: calc(50% - 18px) !important;
    left: 0 !important;
  }
`

const TemplateImageCovered = styled.div`
  padding-bottom: 28.5%;
  height: 0;
  background-size: cover;
  background-position: 50%;
`
const applyGaTrackingOnHtml = (url: string) => (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
  event.preventDefault();
  if (url.includes('http')) {
    // External links tio MN.CO,
    // >> Ga needs updating as it depends on timestamp
    const trackedUrl = reApplyTrackingToUrl(url);
    if (!trackedUrl.includes('_ga')) {
      (window as any).TrackJS && (window as any).TrackJS.console.error('_ga missing!', { url: trackedUrl });
      (window as any).TrackJS && (window as any).TrackJS.track(`_ga missing! ${trackedUrl}`);
    }
    window.location.href = trackedUrl;
  } else {
    // internal are defered to Gatsby (internals are faster)
    navigate(url)
  }
}

const AttachmentContainer = styled.div`
  position: relative;
  width: calc(100% + 200px);
  left: -100px;
  box-sizing: border-box;
  padding: 0 1.2rem;

  > iframe {
    width: 100%;
    height: 510px;
  }

  @media screen and (max-width: ${layout.md}) {
    width: calc(100% + 100px);
    left: -50px;

    > iframe {
      height: 420px;
    }
  }

  @media screen and (max-width: ${layout.subMd}) {
    padding: 0 1.8rem;
  }

  @media screen and (max-width: ${layout.xs}) {
    padding: 0 2.8rem;

    > iframe {
      height: 210px;
    }
  }
`

export type Attachments = ComparisonTableContentfulModel[];

const mapRecursiveHtmlElement = (element: MarkupElement, key: string, attachmentList: Attachments = null, template: Template = null) => element.children.map((child, index) => formatElementFromHtmlAst(child, `${key}-${index}`, attachmentList, template));

const mapMarkdownAttachments = (chunk: string, key: string, attachmentList: Attachments): React.ReactNode => {
  const attachments = chunk.split(/\(\(([0-9)]+)\)\)/g);

  return attachments.map((attachment) => {
    const attachmentIndex = Number(attachment) - 1;

    if (attachmentList[ attachmentIndex ]) {
      const content = attachmentList[ attachmentIndex ];
      const componentRenderer = DynamicComponents[ content.internal.type ];

      if (!componentRenderer) {
        return '';
      }

      return (
        <AttachmentContainer key={content.id}>
          {componentRenderer(content)}
        </AttachmentContainer>
      );
    }

    return isNaN(attachmentIndex) ?
      formatElementFromHtmlAst({ type: 'text', value: attachment }, key, attachmentList) :
      '';
  })
}

interface HtmlAstAnchorElementProps {
  url: string;
  children: React.ReactNode;
  className?: string;
}

const HtmlAstAnchorElement = ({ url, children, className }: HtmlAstAnchorElementProps): JSX.Element => {
  const [ queryPropagatedUrl, updateHref ] = useState(url);

  useEffect(() => {
    if (url) {
      windowIsLoaded().then(() => {
        updateHref(propagateQuerytoExternalLinks(url));
      })
    }
  }, [])

  const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const element = document.querySelector(url);

    if (!element && typeof window === 'undefined') return;
    window.scrollTo({ top: element.getBoundingClientRect().top + window.pageYOffset - 90, behavior: 'smooth' });
  }

  const targetProp = useMemo(() => {
    if (!url) {
      return {};
    }

    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [ url ])

  const navigationProp = url.includes('#') ? { onClick } : { href: queryPropagatedUrl || '#', onClick: applyGaTrackingOnHtml(queryPropagatedUrl || '#') };

  return (
    <a className={className} {...targetProp} {...navigationProp}>
      {children}
    </a>
  )
}

// Returns a formatted HTML element related to the HtmlAst from contentful posts
export const formatElementFromHtmlAst = (element: MarkupElement | MarkupValue, key: string, attachmentList: Attachments = null, template: Template = null): JSX.Element => {
  const elementIsBreakLine = element.type === 'text' && element.value === '\n';

  // Fix multiple breaklines before table (Unknown bug, from contentful maybe?)
  if (element.type === 'text' && element.value.indexOf('\n\n\n\n\n\n\n') !== -1) {
    return null
  }
  if (element.type === 'element') {

    // p initial tag can be either a normal paragraph or a button wrapper
    // >> button wrappers, always have the same construction, p -> stronmg -> a
    // >> This is because buttons don't exist in Markdown
    if (element.tagName === 'p') {
      if (element.children.length === 1 && (element.children[ 0 ] as MarkupElement).tagName === 'strong') {
        const child = element.children[ 0 ] as MarkupElement;
        if (child.children.length === 1 && (child.children[ 0 ] as MarkupElement).tagName === 'a') {
          const linkChild = child.children[ 0 ] as MarkupElement;
          return (
            <p className="center" key={key}>
              <HtmlAstAnchorElement url={linkChild.properties?.href} className="button">
                {(linkChild.children[ 0 ] as MarkupValue).value}
              </HtmlAstAnchorElement>
            </p>
          )
        }
      } else if (element.children.length > 1 && element.children.every((child) => (child as MarkupElement).tagName === 'img')) {
        return (
          <TemplateImagePair key={key}>
            {
              element.children.map((child, childIndex) => (
                <TemplateImageCovered key={`${key}-${childIndex}`} style={{ backgroundImage: `url(${(child as MarkupElement)?.properties?.src})` }} />
              ))
            }
          </TemplateImagePair>
        )
      }

      return (
        <p key={key} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList, template)
          }
        </p>
      )
    }
    else if (element.tagName === 'a') {
      if (template === 'legalArticle') {
        return (
          <PolicyLink to={element.properties?.href}>
            {
              mapRecursiveHtmlElement(element, key, attachmentList, template)
            }
          </PolicyLink>
        )
      }

      const href = element.properties?.href;
      // Embed videos
      if (href?.includes('youtube.com/embed') || href?.includes('player.vimeo.com/video')) {
        return (
          <AttachmentContainer key={key}>
            <iframe
              src={href}
              frameBorder="0"
              allowFullScreen
              id={element.properties?.id}
            />
          </AttachmentContainer>
        )
      }

      return (
        <HtmlAstAnchorElement key={key} url={element.properties?.href}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </HtmlAstAnchorElement>
      )
    }
    else if (element.tagName === 'em') {
      return (
        <em key={key} id={element.properties?.id}>
          {
            element.children.map((child, index) => formatElementFromHtmlAst(child, `${key}-${index}`))
          }
        </em>
      )
    }
    else if (element.tagName === 'blockquote') {
      return (
        <TemplateBlockQuote key={key}>
          {
            element.children.map((child, index) => formatElementFromHtmlAst(child, `${key}-${index}`))
          }
        </TemplateBlockQuote>
      )
    }
    else if (element.tagName === 'h1') {
      return (
        <h1 key={key} style={getStyleObjectFromString(element.properties.style)} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </h1>
      )
    }
    else if (element.tagName === 'h2') {
      return (
        <h2 key={key} style={getStyleObjectFromString(element.properties.style)} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </h2>
      )
    }
    else if (element.tagName === 'h3') {
      return (
        <h3 key={key} style={getStyleObjectFromString(element.properties.style)} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </h3>
      )
    }
    else if (element.tagName === 'h4') {
      return (
        <h4 key={key} style={getStyleObjectFromString(element.properties.style)} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </h4>
      )
    }
    else if (element.tagName === 'h5') {
      return (
        <h5 key={key} style={getStyleObjectFromString(element.properties.style)} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </h5>
      )
    }
    else if (element.tagName === 'strong') {
      return (
        <strong key={key} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </strong>
      )
    }
    else if (element.tagName === 'ul') {
      return (
        <ul key={key} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </ul>
      )
    }
    else if (element.tagName === 'ol') {
      return (
        <ol key={key} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </ol>
      )
    }
    else if (element.tagName === 'li') {
      return (
        <li key={key} id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </li>
      )
    }
    else if (element.tagName === 'img') {
      return (
        <img key={key} src={element.properties.src} id={element.properties?.id} />
      )
    }
    else if (element.tagName === 'table') {
      return (
        <table id={element.properties?.id}>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </table>
      )
    }
    else if (element.tagName === 'thead') {
      return (
        <thead>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </thead>
      )
    }
    else if (element.tagName === 'tbody') {
      return (
        <tbody>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </tbody>
      )
    }
    else if (element.tagName === 'tr') {
      return (
        <tr>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </tr>
      )
    }
    else if (element.tagName === 'th') {
      return (
        <th>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </th>
      )
    }
    else if (element.tagName === 'td') {
      return (
        <td>
          {
            mapRecursiveHtmlElement(element, key, attachmentList)
          }
        </td>
      )
    }
    else if (element.tagName === 'br' || elementIsBreakLine) {
      return <br/>
    }
    else {
      return <span key={key}>{JSON.stringify(element)}</span>
    }
  } else {
    return (
      <span key={key}>
        {element.value.split('__').join('').split('\n').map((chunk, index) => {
          if (attachmentList?.length && chunk.match(/\(\(([0-9)]+)\)\)/g)) {
            return mapMarkdownAttachments(chunk, key, attachmentList);
          }

          return index > 0 ? <React.Fragment key={`${key}--${index}`}><TemplateSpacer />{chunk}</React.Fragment> : <React.Fragment key={`${key}--${index}`}>{chunk}</React.Fragment>
        })}
      </span>
    )
  }
}
